/**
 * Reversible UI overlay dissolve for the Maston connected fleet platform.
 *
 * `data-overlay-visible` is true while this element intersects the viewport and
 * false once it leaves, so the overlay can fade out and dissolve in again.
 * Timing (delay/duration) lives in CSS.
 */
if (!customElements.get('maston-fleet-platform')) {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  class MastonFleetPlatform extends HTMLElement {
    #abortController = null;
    #reducedMotionQuery = null;
    #observer = null;

    connectedCallback() {
      if (this.#abortController) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
      this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange, { signal });

      if (!this.hasAttribute('data-enable-motion') || this.#prefersReducedMotion) {
        this.#showOverlay();
        return;
      }

      this.#observe();
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#observer?.disconnect();
      this.#observer = null;
      this.#reducedMotionQuery = null;
    }

    get #prefersReducedMotion() {
      return Boolean(this.#reducedMotionQuery?.matches);
    }

    #observe() {
      this.#observer?.disconnect();
      this.#observer = null;

      if (!('IntersectionObserver' in window)) {
        this.#showOverlay();
        return;
      }

      this.#observer = new IntersectionObserver(
        ([entry]) => {
          this.toggleAttribute('data-overlay-visible', Boolean(entry?.isIntersecting));
        },
        { threshold: 0 },
      );
      this.#observer.observe(this);
    }

    #showOverlay() {
      this.#observer?.disconnect();
      this.#observer = null;
      this.toggleAttribute('data-overlay-visible', true);
    }

    #onReducedMotionChange = () => {
      if (!this.#prefersReducedMotion) return;
      this.#showOverlay();
    };
  }

  customElements.define('maston-fleet-platform', MastonFleetPlatform);
}
